import { useQuery } from "@apollo/client";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  useDebounce,
  convertToFilter,
  convertArrayToFilterInputObj,
} from "../../../../util";
import Table from "../../../Table";
import { Button, IconButton, Input, Modal, Pagination } from "../..";

import {
  FETCH_INSTANCE as fetchInstance,
  FETCH_INSTANCES_V2 as fetchInstancesV2,
} from "../../../../graphql/query/Instance";
import { FETCH_STATUS } from "../../../../graphql/query/Status";
import MultiSelect from "./MultiSelect";

const LIMIT = 10;

const buildInitialFilters = ({ defaultStatusIds, filterInput }) => {
  const filterInputObj = convertArrayToFilterInputObj(filterInput || []);
  const { statusId: legacyStatusFilter, ...fixedFilterInput } = filterInputObj;
  const legacyStatusIds = Array.isArray(legacyStatusFilter)
    ? legacyStatusFilter
    : legacyStatusFilter?.value || [];

  return {
    limit: LIMIT,
    ...fixedFilterInput,
    statusId: defaultStatusIds ?? legacyStatusIds,
  };
};

const TableCheck = props => {
  const { isMulti, values, selectedValues, onChange } = props;
  const isSelected = selectedValues.some(
    selectedValue => selectedValue?.id === values.id,
  );

  return (
    <div className="relative flex w-[40px] items-start" key={values.id}>
      <div className="flex h-5 items-center">
        <input
          id={`instance-selection-${values.id}`}
          value={values.id}
          checked={isSelected}
          aria-label={`Select instance ${values.name || values.id}`}
          name={isMulti ? `instance-${values.id}` : "selectedInstance"}
          type={isMulti ? "checkbox" : "radio"}
          className={`h-4 w-4 border-2 border-gray-800 text-gray-800 focus:ring-0 ${
            isMulti ? "rounded" : ""
          }`}
          onChange={() => onChange(values)}
        />
      </div>
    </div>
  );
};

const InstanceSearchInput = props => {
  const {
    disabled,
    error,
    warning,
    defaultStatusIds,
    filterInput,
    isMulti = false,
    label,
    loading,
    onChange: setValue,
    redirect = false,
    value,
    warnOverwrite,
    disabledIfFilled,
    emptySelectionLabel,
  } = props;

  const filterContextKey = JSON.stringify({ defaultStatusIds, filterInput });
  const initialFilters = buildInitialFilters({ defaultStatusIds, filterInput });
  const [filters, setFilters] = useState(initialFilters);

  const [displayOverwriteWarning, setDisplayOverwriteWarning] = useState(false);
  const [displayClearWarning, setDisplayClearWarning] = useState(false);

  const [displayTaWarning, setDisplayTaWarning] = useState(false);

  // Keep context filters and their status defaults in sync when the field is
  // reused for a different record.
  useEffect(() => {
    const nextFilters = buildInitialFilters(JSON.parse(filterContextKey));
    setFilters(prevState => ({
      ...nextFilters,
      search: prevState.search,
      offset: 0,
    }));
  }, [filterContextKey]);

  const debouncedFilters = useDebounce(filters, 800);
  const skip = Object.keys(debouncedFilters).length === 0;
  const input = convertToFilter({
    values: debouncedFilters,
    filterKeys: [],
    skipKeys: ["limit", "offset"],
  });

  const {
    data: { instancesV2: { edges = [], count } = {} } = {},
    loading: fetching,
  } = useQuery(
    fetchInstancesV2({
      isBond: {},
      edition: { artwork: { artists: {}, images: { imgT: {} }, title: true } },
      status: {},
    }),
    { skip, variables: { input } },
  );
  const { data: { status = [] } = {}, loading: statusLoading } =
    useQuery(FETCH_STATUS);
  const selectedInstanceId = isMulti ? null : value;
  const { data: { instance = {} } = {} } = useQuery(fetchInstance(), {
    skip: typeof selectedInstanceId !== "number",
    variables: { id: selectedInstanceId },
  });

  const [selectedValues, setSelectedValues] = useState([]);

  useEffect(() => {
    const valueIds = isMulti
      ? Array.isArray(value)
        ? value
        : []
      : value
        ? [value]
        : [];
    setSelectedValues(valueIds.map(id => ({ id })));
  }, [isMulti, value]);

  const onChange = values => {
    setFilters(prevState => ({ ...prevState, ...values }));
  };
  const handleSearchChange = event => {
    setFilters(prevState => ({
      ...prevState,
      search: event.target.value,
      offset: 0,
    }));
  };

  const handleSave = (closeModal, overwrite) => {
    const selectedIds = selectedValues.map(selectedValue => selectedValue.id);
    const nextValue = isMulti ? selectedIds : selectedIds[0] || null;

    if (warnOverwrite && !isMulti && !overwrite && value !== nextValue) {
      setDisplayOverwriteWarning(true);

      if (selectedValues[0]?.isBond) {
        setDisplayTaWarning(true);
      }

      return;
    }

    setValue?.(nextValue);
    closeModal();
  };

  const paginationProps = {
    count,
    limit: LIMIT,
    offset: filters?.offset,
    showSteps: false,
    onChange,
    variant: "simple",
  };

  const handleOnClick = value => {
    setSelectedValues(currentValues => {
      const isSelected = currentValues.some(
        selectedValue => selectedValue.id === value.id,
      );

      if (!isMulti) return [value];
      if (isSelected) {
        return currentValues.filter(
          selectedValue => selectedValue.id !== value.id,
        );
      }
      return [...currentValues, value];
    });
  };

  const statusOptions = status.map(item => ({
    label: item.name,
    value: item.id,
  }));
  const selectedStatusOptions = statusOptions.filter(option =>
    filters.statusId?.includes(option.value),
  );
  const handleStatusChange = selectedOptions => {
    setFilters(prevState => ({
      ...prevState,
      statusId: (selectedOptions || []).map(option => option.value),
      offset: 0,
    }));
  };

  const tableProps = {
    headers: [
      {
        label: "",
        width: "50px",
        name: "checkbox",
        type: "custom",
        component: props => (
          <TableCheck
            {...props}
            isMulti={isMulti}
            selectedValues={selectedValues}
            onChange={handleOnClick}
          />
        ),
      },
      {
        label: "Artwork Image",
        name: "edition.artwork.images[0].imgT",
        type: "image",
      },
      {
        label: "Reference",
        name: "name",
        type: "custom",
        component: props => {
          const value = props.label || "";
          return (
            <Link
              className="underline"
              to={`/instance/${props?.values?.id}/details`}
            >
              {value}
            </Link>
          );
        },
      },
      {
        label: "Title",
        name: "edition.artwork.title",
        type: "label",
        widthInPx: 400,
      },
      {
        label: "Artist Name",
        name: "edition.artwork.artists.[0].name",
        type: "label",
      },
      {
        label: "Status",
        name: "status.name",
        type: "label",
      },
    ],
    limit: LIMIT,
    loading: fetching,
    rows: edges,
    variant: "lg",
  };

  const hasSelection = selectedValues.length > 0;
  const hasExistingValue = isMulti
    ? Array.isArray(value) && value.length > 0
    : Boolean(value);
  const selectedLabel = isMulti
    ? Array.isArray(value) && value.length > 0
      ? `${value.length} instance${value.length === 1 ? "" : "s"} selected`
      : ""
    : instance?.name;

  const modalProps = {
    scale: "md",
    hideCloseButton: true,
    closeOnBackdrop: true,
    disabled: disabled || (disabledIfFilled && hasExistingValue),
    onClose: () => setDisplayOverwriteWarning(false),
    renderBody: ({ closeModal }) => {
      return (
        <>
          <div className="relative flex w-full min-w-0 flex-col">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <div className="flex flex-1 flex-col text-2xl font-bold">
                <div>Select {label || "Instance"}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  action="default"
                  label="Cancel"
                  onClick={() => {
                    closeModal();
                  }}
                />
                <Button
                  action="default"
                  disabled={!hasSelection}
                  label="Reset"
                  onClick={() => {
                    setSelectedValues([]);
                  }}
                />
                <Button
                  disabled={!hasExistingValue}
                  label="Clear"
                  onClick={() => {
                    setDisplayClearWarning(true);
                  }}
                />
                <Button
                  label={"Select"}
                  disabled={loading || fetching || !hasSelection}
                  onClick={() => {
                    handleSave(closeModal, false);
                  }}
                />
              </div>
            </div>
            <div className="pt-2">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-10">
                <Input
                  label="Search"
                  name="search"
                  onChange={handleSearchChange}
                  type="text"
                  value={filters.search || ""}
                />
                <MultiSelect
                  isMulti
                  isClearable
                  isLoading={statusLoading}
                  label="Status"
                  name="statusId"
                  options={statusOptions}
                  selected={selectedStatusOptions}
                  onChange={handleStatusChange}
                />
              </div>
              <div className="max-w-full overflow-x-auto">
                <Table {...tableProps} />
              </div>
              <div>
                <Pagination {...paginationProps} />
              </div>
            </div>
          </div>
          {displayClearWarning && (
            <div className="absolute left-0 top-0 h-full w-full rounded-2xl bg-black/25">
              <div className="absolute left-1/2 top-1/2 w-[calc(100%_-_1rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-4 sm:p-6">
                <p className="mb-4">
                  This will unset the selected instance
                  {isMulti ? "s" : ""}. Are you sure?
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    action="default"
                    label="Cancel"
                    onClick={() => {
                      setDisplayClearWarning(false);
                    }}
                  />
                  <Button
                    label="Confirm"
                    onClick={() => {
                      setSelectedValues([]);
                      setValue?.(isMulti ? [] : null);
                      setDisplayClearWarning(false);
                      closeModal();
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          {displayOverwriteWarning && (
            <div className="absolute left-0 top-0 h-full w-full rounded-2xl bg-black/25">
              <div className="absolute left-1/2 top-1/2 w-[calc(100%_-_1rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-4 sm:p-6">
                <p className="mb-4">
                  Are you sure you want to change the instance this warehouse
                  item is attached to?
                </p>

                {displayTaWarning && (
                  <>
                    <p className="mb-4 font-bold">Customs Control</p>
                    <p className="mb-4">
                      This work is under customs control, you should add this
                      fine art item to the import shipment
                    </p>
                  </>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    action="default"
                    className="sm:mr-6"
                    label="No"
                    onClick={() => {
                      setDisplayOverwriteWarning(false);
                      setDisplayTaWarning(false);
                    }}
                  />
                  <Button
                    label="Yes"
                    onClick={() => {
                      handleSave(closeModal, true);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      );
    },
  };

  if (emptySelectionLabel && !hasExistingValue) {
    return (
      <Modal {...modalProps}>
        <Button label={emptySelectionLabel} disabled={disabled || loading} />
        {error && <small className="text-sm text-red-600">{error}</small>}
      </Modal>
    );
  }

  return (
    <Modal {...modalProps}>
      <div className={`flex flex-1 flex-col`}>
        <div className={`block text-xs font-medium text-black`}>{label}</div>
        <div
          className={`mt-1 flex h-[38px] w-full items-center rounded border border-black focus:border-black focus:ring-0 ${disabled || (disabledIfFilled && hasExistingValue) ? "bg-gray-100" : ""}`}
        >
          <div className={` flex-1 px-3 py-2 text-base `}>
            {redirect && !isMulti ? (
              <Link
                target="_blank"
                className="underline"
                to={`/instance/${instance?.id}/details`}
                onClick={e => e.stopPropagation()}
              >
                {selectedLabel}
              </Link>
            ) : (
              selectedLabel
            )}
          </div>
          <IconButton variant="clean" title="Search Instance">
            <MagnifyingGlassIcon
              className={`h-5 w-5 cursor-pointer text-gray-400`}
            />
          </IconButton>
        </div>
        {error && <small className="text-sm text-red-600">{error}</small>}
        {warning && !error && (
          <small className="text-sm text-red-600">{warning}</small>
        )}
      </div>
    </Modal>
  );
};

export default InstanceSearchInput;
